import { useState, ReactNode, useId, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

// react-router-dom components
import { useLocation, NavLink } from 'react-router-dom';

// @mui material components
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';

import { Grid, IconButton, Tooltip } from '@mui/material';

import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';

// Material Dashboard 2 PRO React TS components
import {
  MDBox,
  MDTypography,
  MDButton,
  FileUploadModal,
  AuthGuard,
  FaIcon,
  Footer,
  MDSocialButton
} from '@components/index';

// Material Dashboard 2 PRO React TS examples components
import SidenavCollapse from './SidenavCollapse';
import SidenavList from './SidenavList';
import SidenavItem from './SidenavItem';

// Custom styles for the Sidenav
import SidenavRoot from './SidenavRoot';
import sidenavLogoLabel from './styles/sidenav';

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from '@context/MDContext';

import { signOut } from '@utils/auth';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { RootState } from '@store/store';

import { Colors } from '@constants/theme';
import { setUserNotifications } from '@components/Notification/slice';
import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  CLICKED_EXPLAINER_VIDEO,
  CLICKED_FEEDBACK,
  CLICKED_HELP,
  CLICKED_MY_PROJECTS,
  CLICKED_SOCIALS,
  CLICKED_UPGRADE_PLAN,
  NEW_PROJECT
} from '@constants/eventNames';
import { getPlanName } from '@utils/common';
import { sendGAData } from '@utils/googleAnalytics';
import useCustomNavigate from '@hooks/useCustomNavigate';
import { PricingPlans } from '@constants/pricing';

import brandLogo from '@assets/images/DS_Logo.png';
import FeedBackModal from '@components/FeedbackModal';
import { useCreateFeedBackMutation } from '@api/projects';

const menuLabelStyles = {
  fontWeight: '300',
  fontSize: '15px'
};

interface BottomMenuItemProps {
  icon: IconProp;
  iconLabel: string;
  onBottomMenuItemClick: () => void;
  needAuthGuard: boolean;
}

// Declaring props types for Sidenav
interface Props {
  color?:
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'dark';
  brand?: string;
  isBannerOpen?: boolean;
  brandName?: string;
  plan?: PricingPlans;
  isTrial?: boolean;
  routes: {
    [key: string]:
      | ReactNode
      | string
      | {
          [key: string]:
            | ReactNode
            | string
            | {
                [key: string]: ReactNode | string;
              }[];
        }[];
  }[];
  [key: string]: any;
}

function Sidenav({
  color,
  brandName = 'DataSquirrel ',
  plan,
  isTrial = false,
  routes,
  isBannerOpen,
  ...rest
}: Props): JSX.Element {
  const planUpgradeUrl = `${process.env.REACT_APP_PLAN_UPGRADE_URL}`;

  const [openCollapse, setOpenCollapse] = useState<boolean | string>(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState<
    boolean | string
  >(false);
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const location = useLocation();
  const { pathname } = location;
  const storeDispatch = useDispatch();

  const collapseName = pathname.split('/').slice(1)[0];
  const items = pathname.split('/').slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const navigate = useCustomNavigate();

  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const projectId = useSelector(
    (state: RootState) => state?.projectBoard?.currentProjectData?.id
  );
  const subscriptionflag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );

  const currentPlanFeatures = useSelector(
    (state: RootState) => state?.AppData.currentPlanFeatures
  );

  let textColor:
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'dark'
    | 'white'
    | 'inherit'
    | 'text'
    | 'light' = 'white';

  if (transparentSidenav || (whiteSidenav && !darkMode)) textColor = 'dark';
  else if (whiteSidenav && darkMode) textColor = 'inherit';

  const tempId = useId();

  const [createFeedBack, { isSuccess }] = useCreateFeedBackMutation();

  const [isFeedBackModalOpen, setIsFeedBackModalOpen] = useState(false);

  const onSubmitFeedBack = (payload: any) => {
    createFeedBack(payload);
  };

  useEffect(() => {
    if (isSuccess) {
      setIsFeedBackModalOpen(false);

      const payload: NotificationData = {
        created_at: dayjs(new Date())
          .utc()
          .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        is_read: false,
        is_silent: false,
        is_user_message: true,
        message_event: MSG_EVENT.FEEDBACK_SUBMITTED,
        message_text:
          'Thank you! Your support request is successfully submitted. Someone from our team will get back to you within 24-48 hours.',
        updated_at: dayjs(new Date())
          .utc()
          .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        message_type: MSG_TYPE.SUCCESS,
        message_data: JSON.stringify({ project: { id: '', name: '' } }),
        linkKeyword: 'here.',
        replaceKeyword: '<click here>',
        is_important: false,
        recipient_obj: 0,
        isAnchorElement: true
      };

      storeDispatch(setUserNotifications(payload));
    }
  }, [isSuccess]);

  // useEffect(() => {
  //   setOpenCollapse(collapseName);
  //   setOpenNestedCollapse(itemParentName);
  // }, []);

  // useEffect(() => {
  //   // A function that sets the mini state of the sidenav.
  //   function handleMiniSidenav() {
  //     setMiniSidenav(dispatch, window.innerWidth < 1200);
  //     setTransparentSidenav(
  //       dispatch,
  //       window.innerWidth < 1200 ? false : transparentSidenav
  //     );
  //     setWhiteSidenav(
  //       dispatch,
  //       window.innerWidth < 1200 ? false : whiteSidenav
  //     );
  //   }

  /**
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
  //   window.addEventListener('resize', handleMiniSidenav);

  //   // Call the handleMiniSidenav function to set the state with the initial value.
  //   handleMiniSidenav();

  //   // Remove event listener on cleanup
  //   return () => window.removeEventListener('resize', handleMiniSidenav);
  // }, [dispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse: any) => {
    const template = collapse.map(({ name, route, key, href }: any) =>
      href ? (
        <Link
          key={key}
          href={href}
          target='_blank'
          rel='noreferrer'
          sx={{ textDecoration: 'none' }}
        >
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} style={{ textDecoration: 'none' }}>
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses: any) =>
    collapses.map(({ name, collapse, route, href, key }: any) => {
      let returnValue;

      if (collapse)
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            active={key === itemParentName ? 'isParent' : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }: any) =>
              openNestedCollapse === key &&
              currentTarget.classList.contains('MuiListItem-root')
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      else
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target='_blank'
            rel='noreferrer'
            sx={{ textDecoration: 'none' }}
          >
            <SidenavItem color={color} name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} style={{ textDecoration: 'none' }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </NavLink>
        );

      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  const secondaryIcons = [
    {
      type: 'collapse',
      name: 'Demo',
      key: 'videolink',
      noCollapse: true,
      icon: 'play',
      width: '60%',
      tooltip: 'Watch our short explainer video',
      href: 'https://www.youtube.com/watch?v=rUVG93r24a8'
    },
    {
      type: 'collapse',
      name: 'Support',
      key: 'feedback',
      noCollapse: true,
      icon: 'message',
      width: '60%',
      tooltip: 'Contact Support',
      onClick: () => {
        setIsFeedBackModalOpen(true);
        sendAmplitudeData(CLICKED_FEEDBACK);
      }
    },
    {
      type: 'collapse',
      name: 'Help',
      key: 'help',
      href: 'https://www.notion.so/datasquirrel/Help-ac1a209929f145c38103062560f661c4',
      noCollapse: true,
      tooltip: 'Click to view Help videos & docs',
      icon: 'circle-question',
      width: '45%'
    },
    {
      type: 'collapse',
      name: 'Logout',
      key: 'logout',
      noCollapse: true,
      icon: 'sign-out',
      width: '52%',
      tooltip: `${userDetails?.user.email}`,
      onClick: () => {
        signOut(`${plan} ${isTrial ? 'Trial' : ''}`);
      }
    }
  ];

  const renderSecondaryIcons = secondaryIcons.map(
    ({
      name,
      icon,
      collapse,
      key,
      href,
      noCollapse,
      tooltip,
      onClick,
      width
    }: any) => {
      let returnValue;

      if (href)
        returnValue = (
          <Link
            href={href}
            key={key}
            target='_blank'
            rel='noreferrer'
            sx={{ textDecoration: 'none' }}
            onClick={() => {
              if (name === 'Help') sendAmplitudeData(CLICKED_HELP);
              if (name === 'Demo') sendAmplitudeData(CLICKED_EXPLAINER_VIDEO);
            }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
              tooltip={tooltip}
              padding={miniSidenav ? '24px' : '10px'}
              miniSideNav={miniSidenav}
              width={miniSidenav ? '45%' : width}
            />
          </Link>
        );
      else
        returnValue = (
          <MDBox onClick={onClick} key={key}>
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() =>
                openCollapse === key
                  ? setOpenCollapse(false)
                  : setOpenCollapse(key)
              }
              tooltip={tooltip}
              width={miniSidenav ? '45%' : width}
              padding={miniSidenav ? '25px' : '10px'}
              miniSideNav={miniSidenav}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          </MDBox>
        );

      return returnValue;
    }
  );

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({
      type,
      name,
      icon,
      title,
      collapse,
      noCollapse,
      key,
      href,
      route,
      text,
      tooltip,
      width
    }: any) => {
      let returnValue;

      if (type === 'collapse')
        if (href)
          returnValue = (
            <Link
              href={href}
              key={key}
              target='_blank'
              rel='noreferrer'
              sx={{ textDecoration: 'none' }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
                tooltip={tooltip}
                miniSideNav={miniSidenav}
                width={width}
              />
            </Link>
          );
        else if (noCollapse && route)
          returnValue = (
            <NavLink
              to={route}
              key={key}
              onClick={() => {
                if (name === 'My Projects')
                  sendAmplitudeData(CLICKED_MY_PROJECTS);
                navigate(route);
              }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
                tooltip={tooltip}
                miniSideNav={miniSidenav}
                width={width}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        else
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() =>
                openCollapse === key
                  ? setOpenCollapse(false)
                  : setOpenCollapse(key)
              }
              miniSideNav={miniSidenav}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
      else if (type === 'title')
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display='block'
            variant='caption'
            fontWeight='bold'
            textTransform='uppercase'
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      else if (type === 'divider')
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      else if (type === 'text')
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            variant='caption'
            fontWeight='normal'
            pl={3}
            mt={2}
            mb={1}
            ml={1}
            mr={1}
          >
            {text}
          </MDTypography>
        );

      return returnValue;
    }
  );

  const bottomMenuItems: BottomMenuItemProps[] = [
    {
      icon: 'bell',
      iconLabel: 'Help',
      onBottomMenuItemClick: () => null,
      needAuthGuard: true
    },
    {
      icon: 'message',
      iconLabel: 'Feedback',
      onBottomMenuItemClick: () => null,
      needAuthGuard: false
    },
    {
      icon: 'bell',
      iconLabel: 'Msg',
      onBottomMenuItemClick: () => null,
      needAuthGuard: true
    },
    {
      icon: 'sign-out',
      iconLabel: 'Logout',
      onBottomMenuItemClick: signOut,
      needAuthGuard: false
    },
    {
      icon: 'user',
      iconLabel: 'User',
      onBottomMenuItemClick: () => null,
      needAuthGuard: true
    }
  ];

  useEffect(() => {
    if (window.location.href.includes('?upload=1')) setIsUploadModalOpen(true);
  }, []);

  const handleNewButtonClick = () => {
    const updatedUrl = window.location.href + '?upload=1';

    window.history.pushState({ path: updatedUrl }, '', updatedUrl);

    sendAmplitudeData(NEW_PROJECT);
    sendGAData(NEW_PROJECT);
    setIsUploadModalOpen(true);
  };

  const handleClose = () => {
    let updatedUrl = window.location.href;

    if (updatedUrl.includes('?upload=1')) {
      updatedUrl = window.location.href.split('?upload=1')?.[0];

      window.history.pushState({ path: updatedUrl }, '', updatedUrl);
    }

    setIsUploadModalOpen(false);
  };

  const handleUpgradeClick = () => {
    if (subscriptionflag) {
      sendAmplitudeData(CLICKED_UPGRADE_PLAN, {
        current_plan: `${userDetails?.plan} ${isTrial ? '_TRIAL' : ''}`,
        is_trial_utilised: userDetails?.has_used_trial ? 'yes' : 'no',
        project_id: projectId
      });
      window.location.replace(
        `${planUpgradeUrl}?userid=${userDetails?.user?.id}&plan_level=PRO${
          projectId ? `&project_id=${projectId}` : ''
        }`
      );
    }
  };

  const planNameTooltip = useMemo(() => {
    const projectUploadLimit = currentPlanFeatures?.projectLimit;
    const squirbyLimit = currentPlanFeatures?.squirbyLimit;
    const insightsLimit = currentPlanFeatures?.insightsLimit;
    const gSheetUpdateLimit = currentPlanFeatures?.gSheetUpdateLimit;
    const apiUpdateLimit = currentPlanFeatures?.apiUpdateLimit;

    const projectLimitsLeft = projectUploadLimit
      ? `Project Credits: ${
          !isNaN(userDetails?.project_monthly_limit_remaining as number)
            ? userDetails?.project_monthly_limit_remaining
            : projectUploadLimit - userDetails?.project_count || 0
        }/${projectUploadLimit} remaining`
      : '';

    const squirbyLimitsLeft = squirbyLimit
      ? `Squirby Credits: ${
          userDetails?.squirby_limit_remaining || 0
        }/${squirbyLimit} remaining`
      : '';
    const insightsLimitLeft = insightsLimit
      ? `AI Insights Credits: ${
          userDetails?.insights_limit_remaining || 0
        }/${insightsLimit} remaining`
      : '';

    const gsheetUpdatesLeft = gSheetUpdateLimit
      ? `Google Sheet Update Credits: ${
          userDetails?.data_updates_remaining || 0
        }/${gSheetUpdateLimit} remaining`
      : '';

    const apiUpdatesLeft = apiUpdateLimit
      ? `Data Update for API Credits: ${
          userDetails?.api_updates_remaining || 0
        }/${apiUpdateLimit} remaining`
      : '';

    return (
      <div style={{ textAlign: 'left' }}>
        <div>{projectLimitsLeft}</div>
        <div>{squirbyLimitsLeft}</div>
        <div>{insightsLimitLeft}</div>
        <div>{gsheetUpdatesLeft}</div>
        <div>{apiUpdatesLeft}</div>
      </div>
    );
  }, [userDetails, currentPlanFeatures]);

  return (
    <>
      <SidenavRoot
        {...rest}
        variant='permanent'
        ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
        PaperProps={{
          sx: {
            marginY: isBannerOpen ? 7 : 1,
            marginX: 1
          }
        }}
      >
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: isBannerOpen ? '78vh' : '100vh',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Grid item>
            <MDBox
              px={2}
              textAlign='center'
              display={miniSidenav ? 'none' : 'block'}
            >
              {/* <MDBox
                display={{ xs: 'block', xl: 'none' }}
                position='absolute'
                top={0}
                right={0}
                p={1.625}
                onClick={closeSidenav}
                sx={{ cursor: 'pointer' }}
              >
                <MDTypography variant='h6' color='secondary'>
                  <Icon sx={{ fontWeight: 'bold' }}>close</Icon>
                </MDTypography>
              </MDBox> */}
              <MDBox
                component={NavLink}
                to='/'
                display='flex'
                alignItems='flex-end'
                justifyContent='center'
              >
                <img src={brandLogo} alt='Brand' style={{ width: '2rem' }} />
                <MDBox
                  width={!miniSidenav ? 'auto' : ''}
                  sx={(theme: any) => sidenavLogoLabel(theme, { miniSidenav })}
                >
                  <MDTypography
                    variant='h5'
                    fontWeight='medium'
                    color={Colors.DARK}
                    sx={{ lineHeight: 1 }}
                  >
                    {brandName}
                    {/* <MDTypography
                      variant='xs'
                      sx={{
                        display: 'inline',
                        verticalAlign: 'super'
                      }}
                      fontWeight='medium'
                      color={Colors.DARK}
                    >
                      BETA
                    </MDTypography> */}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
            {miniSidenav ? (
              <MDBox textAlign='center' pt={1} px={2}>
                <MDBox component={NavLink} to='/'>
                  <img src={brandLogo} alt='Brand' style={{ width: '2rem' }} />
                </MDBox>
              </MDBox>
            ) : (
              <></>
            )}

            {miniSidenav ? (
              <MDBox textAlign='center' mb={2} mt='1.7rem' width='94%' pl='3px'>
                <Tooltip title='Click to create new project' placement='right'>
                  <IconButton onClick={handleNewButtonClick}>
                    <FaIcon
                      typographyProps={{ variant: 'xl', color: 'primary' }}
                      icon='plus-square'
                    />
                  </IconButton>
                </Tooltip>
              </MDBox>
            ) : (
              <MDBox textAlign='center' mb={2} mt='1.8rem' mx={2}>
                <Tooltip title='Click to create new project' placement='right'>
                  <MDButton
                    color='light'
                    onClick={handleNewButtonClick}
                    size='medium'
                  >
                    <FaIcon
                      typographyProps={{ variant: 'xs', color: 'primary' }}
                      icon='plus-square'
                    />
                    <MDTypography pl={1} variant='xs' color='info'>
                      NEW PROJECT
                    </MDTypography>
                  </MDButton>
                </Tooltip>
              </MDBox>
            )}
            <List sx={{ marginX: miniSidenav ? '-10px' : '8px' }}>
              {renderRoutes}
            </List>
            {subscriptionflag && (
              <Divider
                variant={'fullWidth'}
                sx={{ opacity: 1, height: '1px', marginTop: '25px' }}
              />
            )}
            {miniSidenav ? (
              <>
                {subscriptionflag && plan && (
                  <MDBox textAlign='center' margin='1.8rem 2px 2px 1px'>
                    <Tooltip title={planNameTooltip} placement='left'>
                      <MDBox width={'45%'} pl='36px' pr={'26px'}>
                        <FaIcon
                          typographyProps={{ variant: 'lg', color: 'info' }}
                          icon='circle-dollar'
                        />
                      </MDBox>
                    </Tooltip>
                  </MDBox>
                )}
              </>
            ) : (
              <>
                {subscriptionflag && plan && (
                  <Tooltip title={planNameTooltip} placement='left'>
                    <MDBox
                      // textAlign='left'
                      // margin='1.8rem 2px 2px 7.5px'
                      // width={'max-content'}
                      textAlign='left'
                      // mb={2}
                      mt='1.8rem'
                      mr={2}
                      ml={7.5}
                      width={'max-content'}
                    >
                      <FaIcon
                        typographyProps={{ variant: 'sm', color: 'info' }}
                        icon='circle-dollar'
                      />
                      <MDTypography
                        pl={1}
                        variant='sm'
                        color='info'
                        sx={{ fontWeight: 500 }}
                      >
                        {getPlanName(plan, isTrial)}
                      </MDTypography>
                    </MDBox>
                  </Tooltip>
                )}
              </>
            )}
            {!miniSidenav && currentPlanFeatures?.projectLimit > 0 && (
              <MDBox display='flex' alignItems='center' mb={3} ml={3.5}>
                <MDTypography
                  pl={1}
                  variant='sm'
                  color='info'
                  sx={{ fontWeight: 500 }}
                >
                  {`Project Credits: ${
                    !isNaN(
                      userDetails?.project_monthly_limit_remaining as number
                    )
                      ? userDetails?.project_monthly_limit_remaining
                      : (currentPlanFeatures?.projectLimit || 0) -
                        userDetails?.project_count
                  }/${currentPlanFeatures?.projectLimit}`}
                </MDTypography>
              </MDBox>
            )}
            {subscriptionflag && (
              <>
                {miniSidenav ? (
                  <MDBox textAlign='center' mb={2} onClick={handleUpgradeClick}>
                    <IconButton>
                      <Tooltip title='Click to upgrade plan' placement='left'>
                        <MDBox width={'45%'} pr='30px'>
                          <FaIcon
                            typographyProps={{
                              variant: 'xl',
                              color: 'primary'
                            }}
                            icon='rocket'
                          />
                        </MDBox>
                      </Tooltip>
                    </IconButton>
                  </MDBox>
                ) : (
                  <MDBox
                    textAlign='center'
                    mb={2}
                    mx={2}
                    mt={currentPlanFeatures?.projectLimit > 0 ? 0 : 3}
                  >
                    <Tooltip title='Click to upgrade plan' placement='left'>
                      <MDButton
                        color='light'
                        size='medium'
                        onClick={handleUpgradeClick}
                      >
                        <FaIcon
                          typographyProps={{ variant: 'xs', color: 'primary' }}
                          icon='rocket'
                        />
                        <MDTypography pl={1} variant='xs' color='info'>
                          UPGRADE PLAN
                        </MDTypography>
                      </MDButton>
                    </Tooltip>
                  </MDBox>
                )}
              </>
            )}
            <Divider
              variant={'fullWidth'}
              sx={{ opacity: 1, height: '1px', marginTop: '30px' }}
            />
            <List sx={{ marginX: miniSidenav ? '-10px' : '8px' }}>
              {renderSecondaryIcons}
            </List>

            <Divider
              variant={'fullWidth'}
              sx={{ opacity: 1, height: '1px', marginTop: '30px' }}
            />
          </Grid>

          <Grid item>
            <Divider
              light={
                (!darkMode && !whiteSidenav && !transparentSidenav) ||
                (darkMode && !transparentSidenav && whiteSidenav)
              }
            />
            <MDBox
              mb={2}
              pl={5}
              display={{ xs: 'flex', sm: 'none' }}
              sx={{ flexDirection: 'column', rowGap: 2 }}
            >
              {bottomMenuItems.map((bottomMenuItem, index) => (
                <AuthGuard
                  is_demo_user={userDetails.user.is_demo_user}
                  needAuthGuard={bottomMenuItem.needAuthGuard}
                  key={index}
                >
                  <MDBox
                    sx={{
                      display: 'flex',
                      columnGap: 1.5,
                      alignItems: 'center'
                    }}
                    width='fit-content'
                    onClick={bottomMenuItem.onBottomMenuItemClick}
                  >
                    <FaIcon
                      typographyProps={{ varaint: 'xs', color: 'dark' }}
                      icon={bottomMenuItem.icon}
                    />

                    <MDTypography
                      pl={1}
                      variant='dark'
                      sx={{ ...menuLabelStyles }}
                    >
                      {bottomMenuItem.iconLabel}
                    </MDTypography>
                  </MDBox>
                </AuthGuard>
              ))}
            </MDBox>
          </Grid>
        </Grid>
        {miniSidenav ? (
          <Grid
            container
            display='flex'
            flexDirection='column'
            gap={1}
            my={2}
            justifyContent='center'
            justifyItems='center'
            alignItems='center'
          >
            <Grid
              item
              onClick={() =>
                sendAmplitudeData(CLICKED_SOCIALS, {
                  social_channel: 'X'
                })
              }
            >
              <a
                href='https://twitter.com/datasquirrel_ai'
                target='_blank'
                rel='noreferrer'
              >
                <MDSocialButton color='twitter' iconOnly size='small'>
                  <TwitterIcon />
                </MDSocialButton>
              </a>
            </Grid>
            <Grid
              item
              onClick={() =>
                sendAmplitudeData(CLICKED_SOCIALS, {
                  social_channel: 'linkedIn'
                })
              }
            >
              <a
                href='https://www.linkedin.com/company/datasquirrel-ai/'
                target='_blank'
                rel='noreferrer'
              >
                <MDSocialButton color='linkedin' iconOnly size='small'>
                  <LinkedInIcon />
                </MDSocialButton>
              </a>
            </Grid>
            <Grid
              item
              onClick={() =>
                sendAmplitudeData(CLICKED_SOCIALS, {
                  social_channel: 'instagram'
                })
              }
            >
              <a
                href='https://www.instagram.com/datasquirrel.ai'
                target='_blank'
                rel='noreferrer'
              >
                <MDSocialButton
                  iconOnly
                  size='small'
                  sx={{
                    background:
                      'radial-gradient(circle farthest-corner at 35% 90%, #fec564, transparent 50%), radial-gradient(circle farthest-corner at 0 140%, #fec564, transparent 50%), radial-gradient(ellipse farthest-corner at 0 -25%, #5258cf, transparent 50%), radial-gradient(ellipse farthest-corner at 20% -50%, #5258cf, transparent 50%), radial-gradient(ellipse farthest-corner at 100% 0, #893dc2, transparent 50%), radial-gradient(ellipse farthest-corner at 60% -20%, #893dc2, transparent 50%), radial-gradient(ellipse farthest-corner at 100% 100%, #d9317a, transparent), linear-gradient(#6559ca, #bc318f 30%, #e33f5f 50%, #f77638 70%, #fec66d 100%)'
                  }}
                >
                  <InstagramIcon />
                </MDSocialButton>
              </a>
            </Grid>
            <Grid
              item
              onClick={() =>
                sendAmplitudeData(CLICKED_SOCIALS, {
                  social_channel: 'facebook'
                })
              }
            >
              <a
                href=' https://www.facebook.com/datasquirrel.hq '
                target='_blank'
                rel='noreferrer'
              >
                <MDSocialButton color='facebook' iconOnly size='small'>
                  <FacebookIcon />
                </MDSocialButton>
              </a>
            </Grid>
          </Grid>
        ) : (
          <Grid container display={{ xs: 'none', sm: 'flex' }}>
            <Footer />
          </Grid>
        )}

        <Grid container display={{ xs: 'flex', sm: 'none' }} my={1}>
          <Grid
            container
            direction='row'
            spacing={2}
            sx={{ justifyContent: 'center' }}
          >
            <Grid item>
              <a
                href='https://twitter.com/datasquirrel_ai'
                target='_blank'
                rel='noreferrer'
              >
                <TwitterIcon htmlColor='#17375F' />
              </a>
            </Grid>
            <Grid item>
              <a
                href='https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fdatasquirrel.ai'
                target='_blank'
                rel='noreferrer'
              >
                <LinkedInIcon htmlColor='#17375F' />
              </a>
            </Grid>
            <Grid item>
              <a
                href='https://www.instagram.com/datasquirrel.ai'
                target='_blank'
                rel='noreferrer'
              >
                <InstagramIcon htmlColor='#17375F' />
              </a>
            </Grid>
            <Grid item>
              <a
                href=' https://www.facebook.com/datasquirrel.hq '
                target='_blank'
                rel='noreferrer'
              >
                <FacebookIcon htmlColor='#17375F' />
              </a>
            </Grid>
          </Grid>
        </Grid>
      </SidenavRoot>
      {isUploadModalOpen && (
        <FileUploadModal
          open={isUploadModalOpen}
          onClose={handleClose}
          handleFileUploadSuccess={(isGoogleSheet: boolean) => {
            const payload: NotificationData = {
              created_at: dayjs(new Date())
                .utc()
                .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
              id: dayjs(new Date())
                .utc()
                .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
              is_read: false,
              is_silent: false,
              is_user_message: true,
              message_event: MSG_EVENT.FILE_PROCESSING,
              message_text: `We are processing your ${
                isGoogleSheet ? 'Google Sheet' : 'file'
              }. Please check back the status of your file in <click here>`,
              updated_at: dayjs(new Date())
                .utc()
                .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
              message_type: MSG_TYPE.SUCCESS,
              message_data: JSON.stringify({ project: { id: tempId || '' } }),
              is_important: false,
              recipient_obj: 0,
              replaceKeyword: '<click here>',
              linkKeyword: 'My Projects.'
            };

            storeDispatch(setUserNotifications(payload));
          }}
        />
      )}
      {isFeedBackModalOpen && (
        <FeedBackModal
          open={isFeedBackModalOpen}
          onClose={() => setIsFeedBackModalOpen(false)}
          onSubmitFeedBack={onSubmitFeedBack}
        />
      )}
    </>
  );
}

export default Sidenav;
